/**
 * Phần THUẦN của màn "Gộp hồ sơ trùng" (STT 2.5, ADR-091): chọn chiều mặc định, bật / tắt "Tiếp tục", dựng thân yêu cầu,
 * dịch cảnh báo của máy chủ thành chữ. Không React, không mạng — để test đơn vị chạy được.
 *
 * Máy chủ mới là nơi quyết định gộp được hay không (kiểm lại trong giao dịch); client chỉ chọn chiều và hiện kết quả.
 */
import { formatVnDate, formatVnTime, parseIsoStrict } from '@sapa/shared'
import type { CanhBaoGop, XemTruocChieu, XemTruocGop } from './api.ts'
import { GK } from './text/gop-khach.ts'

export type Chieu = 'a' | 'b'

export const chieuKia = (c: Chieu): Chieu => (c === 'a' ? 'b' : 'a')

/** Kết quả của chiều `c`: 'a' = giữ hồ sơ a (gộp b). */
export function ketQuaChieu(x: XemTruocGop, c: Chieu): XemTruocChieu {
  return c === 'a' ? x.giuA : x.giuB
}

/**
 * Chiều mặc định: máy chủ gợi ý (`goiY`); không có thì chiều nào gộp được; không chiều nào thì 'a' (chỉ để hiện lý do).
 * Không bao giờ chọn sẵn một chiều KHÔNG gộp được khi chiều kia gộp được.
 */
export function chieuMacDinh(x: XemTruocGop): Chieu {
  if (x.goiY !== null) return x.goiY
  if (x.giuA.duocGop) return 'a'
  if (x.giuB.duocGop) return 'b'
  return 'a'
}

/** Nút "Giữ hồ sơ kia" chỉ hiện khi chiều kia gộp được (đổi chiều không gọi lại máy chủ). */
export function coTheDoiChieu(x: XemTruocGop, c: Chieu): boolean {
  return ketQuaChieu(x, chieuKia(c)).duocGop
}

/** "Tiếp tục" sáng khi chiều này gộp được VÀ (không cần chọn số, hoặc đã chọn). */
export function coTheTiepTuc(k: XemTruocChieu, soGiu: 'giu' | 'bo' | null): boolean {
  if (!k.duocGop) return false
  return !k.canChonSo || soGiu !== null
}

/** Thân `POST /v1/admin/khach/gop`. `soGiu` chỉ có khi hai số khác nhau; ngược lại LUÔN `null` (máy chủ 400 nếu sai). */
export function thanGop(
  x: XemTruocGop,
  c: Chieu,
  soGiu: 'giu' | 'bo' | null,
): { giuId: string; boId: string; soGiu: 'giu' | 'bo' | null } {
  const giu = c === 'a' ? x.a : x.b
  const bo = c === 'a' ? x.b : x.a
  const can = ketQuaChieu(x, c).canChonSo
  return { giuId: giu.id, boId: bo.id, soGiu: can ? soGiu : null }
}

/** "06/10 10:20" theo giờ Việt Nam (từ ISO của máy chủ). */
export function gioVietGop(iso: string): string {
  const dt = parseIsoStrict(iso)
  return `${formatVnDate(dt).slice(0, 5)} ${formatVnTime(dt)}`
}

export type MucCanhBao = 'nang' | 'nhe'

export interface DongCanhBao {
  ma: CanhBaoGop['ma']
  /** `nang` = lộ dữ liệu / mất dữ liệu không hoàn tác (đỏ, đậm); `nhe` = hệ quả cần biết (vàng). */
  muc: MucCanhBao
  chu: string
}

/**
 * Thứ bậc: Zalo vào hồ sơ có thứ để lộ, ưu đãi mất, ảnh bị xoá = NẶNG; chờ xác nhận và lịch trùng giờ = NHẸ
 * (chỉ báo hậu quả, không chặn).
 */
const MUC_CANH_BAO: Record<CanhBaoGop['ma'], MucCanhBao> = {
  'zalo-chuyen': 'nang',
  'zalo-giu': 'nang',
  'uu-dai': 'nang',
  'anh-se-xoa': 'nang',
  'cho-xac-nhan': 'nhe',
  'lich-trung': 'nhe',
}

/**
 * Cảnh báo (mã máy → dòng có thứ bậc), NẶNG trước. `tenGiu` là hồ sơ SẼ CÒN, `tenBo` là hồ sơ bị ẩn. Lịch chồng giờ: mỗi
 * cặp một dòng (tối đa 5 do máy chủ cắt). `ngan` = bản ngắn nhắc lại ở bước xác nhận.
 */
export function dongCanhBao(
  canhBao: CanhBaoGop[],
  tenGiu: string,
  tenBo: string,
  tuyChon: { gio?: (iso: string) => string; ngan?: boolean } = {},
): DongCanhBao[] {
  const ra: DongCanhBao[] = canhBao.flatMap((c) =>
    chuMotCanhBao(c, tenGiu, tenBo, tuyChon.gio ?? gioVietGop, tuyChon.ngan === true).map(
      (chu) => ({
        ma: c.ma,
        muc: MUC_CANH_BAO[c.ma],
        chu,
      }),
    ),
  )
  // NẶNG trước, giữ thứ tự gốc trong cùng mức.
  return [...ra.filter((d) => d.muc === 'nang'), ...ra.filter((d) => d.muc === 'nhe')]
}

/** Chữ của MỘT cảnh báo (lịch trùng giờ cho ra một dòng mỗi cặp). */
function chuMotCanhBao(
  c: CanhBaoGop,
  tenGiu: string,
  tenBo: string,
  gio: (iso: string) => string,
  ngan: boolean,
): string[] {
  const n = c.n ?? 0
  switch (c.ma) {
    case 'zalo-chuyen':
      return [ngan ? GK.ngan.zaloChuyen(tenBo, tenGiu) : GK.canhBaoZalo(tenBo, tenGiu)]
    case 'cho-xac-nhan':
      return [ngan ? GK.ngan.choXacNhan(tenGiu) : GK.canhBaoChoXacNhan(tenGiu)]
    case 'zalo-giu':
      return [
        ngan
          ? GK.ngan.zaloGiu(tenGiu, n, c.soGan === true)
          : GK.canhBaoZaloGiu(tenGiu, n, c.soGan === true),
      ]
    case 'uu-dai':
      return [ngan ? GK.ngan.uuDai(tenBo) : GK.canhBaoUuDai(tenBo)]
    case 'lich-trung':
      return (c.cap ?? []).map((p) =>
        ngan
          ? GK.ngan.lichTrung(gio(p.tuLuc), p.codeA, p.codeB)
          : GK.canhBaoLichTrung(gio(p.tuLuc), p.codeA, p.codeB),
      )
    case 'anh-se-xoa':
      return [ngan ? GK.ngan.anh(n) : GK.canhBaoAnh(n)]
  }
}

/** Chữ của các cảnh báo (cùng thứ tự `dongCanhBao`) — giữ cho test cũ / nơi chỉ cần chuỗi. */
export function chuCanhBao(
  canhBao: CanhBaoGop[],
  tenGiu: string,
  tenBo: string,
  gio: (iso: string) => string = gioVietGop,
): string[] {
  return dongCanhBao(canhBao, tenGiu, tenBo, { gio }).map((d) => d.chu)
}

/** Một chiều bị chặn vì hồ sơ BỊ ẨN của chiều đó có tiền / gói — lý do máy chọn chiều kia. */
export function chieuBiChanViTien(k: XemTruocChieu): boolean {
  return !k.duocGop && (k.maLyDo === 'bo-co-tien' || k.maLyDo === 'bo-co-coc')
}

/** Nhãn nguồn của một thẻ: hồ sơ đang mở (a) hay hồ sơ kia (b). */
export function nhanNguon(id: string, x: XemTruocGop): string {
  return id === x.a.id ? GK.hoSoDangMo : GK.hoSoKia
}

/**
 * Câu hậu quả của lựa chọn số, hoặc câu nhắc chung khi chưa chọn. Số KHÔNG được chọn nằm vào Lưu ý (không còn tìm
 * khách được) — nói rõ bằng đúng số đó (kèm hồ sơ nào sẽ mất số).
 */
export function cauHauQuaSo(
  k: XemTruocChieu,
  soGiu: 'giu' | 'bo' | null,
  hienSo: (s: string | null) => string,
): string {
  if (k.ketQua === null || soGiu === null) return GK.goiYSo
  const bi = soGiu === 'giu' ? k.ketQua.phoneTheoSoGiu.bo : k.ketQua.phoneTheoSoGiu.giu
  return GK.hauQuaSo(hienSo(bi))
}

/** Bấm "Gộp hai hồ sơ" luôn nhận được; chưa tích mà phải tích thì nhắc (cuộn tới ô tích), không âm thầm tắt. */
export function viecKhiBamGop(canTick: boolean, daTick: boolean): 'gop' | 'nhac-tich' {
  return coTheGopThat(canTick, daTick) ? 'gop' : 'nhac-tich'
}

/** Dòng "Số" của khối "Sau khi gộp": chưa chọn số thì nói "Chọn số ở trên" (không "Chưa có số" — như mất số). */
export function chuSoSauGop(
  soDaChon: string | null,
  canChonSo: boolean,
  hienSo: (s: string | null) => string,
): string {
  if (soDaChon === null) return canChonSo ? GK.chonSoOTren : GK.chuaCoSo
  return hienSo(soDaChon)
}

/**
 * Có Zalo ở BẤT KỲ bên nào → bước xác nhận đòi một ô tích riêng ("khách đang ở tiệm và đã mở Zalo cho tôi xem"):
 * gộp hai hồ sơ là gộp quyền xem gói / ví / lịch của hai người, không chỉ bấm nút (N1 + N2).
 */
export function canTickXacNhanZalo(x: XemTruocGop): boolean {
  return x.a.coZalo || x.b.coZalo
}

/** Nút "Gộp hai hồ sơ" bấm được khi (không cần tích) hoặc (đã tích). */
export function coTheGopThat(canTick: boolean, daTick: boolean): boolean {
  return !canTick || daTick
}

/** Chữ lỗi khi gộp: mất mạng / 5xx → câu "mạng chậm" (GIỮ khoá); còn lại câu của máy chủ. */
export function cauLoiGop(e: { status: number; messageVi: string }): {
  cau: string
  giuKhoa: boolean
} {
  if (e.status === 0 || e.status >= 500) return { cau: GK.mangCham, giuKhoa: true }
  if (e.status === 429) return { cau: e.messageVi, giuKhoa: true }
  return { cau: e.messageVi.trim() === '' ? GK.loiChung : e.messageVi, giuKhoa: false }
}

/** "2026-10-05T…Z" hoặc "1990-03-04" → "05/10/2026" theo giờ VN (ngày sinh không đổi múi giờ). */
export function ngayVietGop(raw: string, theoGioVn: (iso: string) => string): string {
  const ngay = raw.length === 10 ? raw : theoGioVn(raw)
  return `${ngay.slice(8, 10)}/${ngay.slice(5, 7)}/${ngay.slice(0, 4)}`
}

/** Hai số khác nhau (cần chọn) → dòng "Số khác" hiện ở xem trước đã nằm ở máy chủ; ở client chỉ cần chọn đúng nhãn. */
export function soCuaLuaChon(k: XemTruocChieu, soGiu: 'giu' | 'bo' | null): string | null {
  if (k.ketQua === null) return null
  const s = k.ketQua.phoneTheoSoGiu
  if (!k.canChonSo) return s.giu ?? s.bo
  if (soGiu === null) return null
  return soGiu === 'giu' ? s.giu : s.bo
}
